import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import {
  Button, 
  Toolbar, 
  IconButton, 
  Typography, 
  MenuItem,
  AppBar,
  Grid,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Icon from '@mdi/react';
import {
  mdiContentSave,
  mdiEmailEditOutline,
  mdiInboxArrowDown,
  mdiMenuDown,
  mdiPlaySpeed,
  mdiProgressWrench,
  mdiRestore,
  mdiUndo,
} from '@mdi/js';
import Menu from '../CommonComponents/Menu';
import Snackbar from '../CommonComponents/Snackbar';
import { useTranslation } from "react-i18next";
import {RequestStatusId, UserRole} from '../../ApplicationConfig/constants';
import ActionsMenu from './RequestContextMenu';
import {UserInfoContext} from '../../StateObjects/UserInfoContext';

import {
  DialogWithdraw,
  DialogUpdate, 
  DialogApproved, 
  DialogDenied,
  DialogAssign
} from '../CommonComponents/DialogBox';

const PREFIX = 'RequestToolBar';

const classes = {
  title: `${PREFIX}-title`,
  headerBtn: `${PREFIX}-headerBtn`,
  appBar: `${PREFIX}-appBar`,
  gridContainer: `${PREFIX}-gridContainer`,
  actions: `${PREFIX}-actions`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.title}`]: {
    paddingLeft: theme.spacing(1),
  },

  [`& .${classes.headerBtn}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.appBar}`]: {
    justifyContent: 'center',
    width: 'auto',
    minHeight: theme.spacing(8),
    backgroundColor: theme.palette.common.white,
    boxShadow: 'none',
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.divider,
    top: 'auto',
    left: 0,
  },

  [`& .${classes.gridContainer}`]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },

  [`& .${classes.actions}`]: {
    display: 'flex',
    alignItems: 'center',
  }
}));

export default function RequestToolBar(props) {

  const {t, i18n} = useTranslation();
  const {userInfo} = React.useContext(UserInfoContext);
  const {
    handleSubmit,
    handleRequestUpdate,
    handleApproveRequest,
    handleDenyRequest,
    handleSave,
    handleValidateRequest,
    handleAssignMe,
    handleUnassign,
    handleSaveBeforeLeave,
    handleDelete,
    handleWithdraw,
    handleReactivate,
    handleStartReview,
    isRequester,
    isLeadAnalyst,
    isSupportAnalyst,
    role,
    readOnly,
    request,
    handleRefreshForm,
    setBackdropOpen,
    snackbarState,
    handleCloseSnackbar
  }=props;
  const [open, setOpen] = React.useState({
    dialogUpdate: false,
    dialogApproved: false,
    dialogDenied: false,
    dialogWithdraw: false,
    dialogAssign: false
  });
  const [anchorEl, setAnchorEl] = React.useState(null);

  const toggleDialog = (state, value) => {
    setOpen({...open,[state]: value})
  };

  const handleBlur=(event)=>{
    if (event.key === 'Tab') {
      event.preventDefault();
      setAnchorEl(null);
    }
  }

  const saveBtn=()=>{
    if(!readOnly && 
    (request.status.id === RequestStatusId.Draft ||
    request.status.id === RequestStatusId.UpdateRequest ||
    request.status.id === RequestStatusId.DisclosureAnalysis)){
      return (
        <Button
          id="toolbar-save-btn"
          variant="outlined"
          color="primary"
          className={classes.headerBtn}
          startIcon={<Icon path={mdiContentSave} size={1} />}
          onClick={handleSave}
        >
          {t("requestForm.toolbar.save")}
        </Button>
      );
    }
  }

  const submitBtn=()=>{
    if(!readOnly &&
    (request.status.id === RequestStatusId.Draft ||
    request.status.id === RequestStatusId.UpdateRequest)){
      return (
        <Button
          id="toolbar-submit-btn"
          variant="contained"
          color="primary"
          className={classes.headerBtn}
          startIcon={<Icon path={mdiInboxArrowDown} size={1} />}
          onClick={handleSubmit}
        >
          {t("requestForm.toolbar.submit")}
        </Button>
      );
    }
  }

  const requestUpdateBtn=()=>{
    if(request.status.id===RequestStatusId.DisclosureAnalysis){
      return (
        <Button
          id="toolbar-requestUpdate-btn"
          variant="outlined"
          color="primary"
          className={classes.headerBtn}
          startIcon={<Icon path={mdiEmailEditOutline} size={1} />}
          onClick={()=> {
            handleCloseSnackbar("snackbarUpdate");
            toggleDialog('dialogUpdate',true);
          }}
        >
          {t("requestForm.toolbar.requestUpdate")}
        </Button>
      );
    }
  }

  const resolveBtn=()=>{
    if(request.status.id===RequestStatusId.DisclosureAnalysis){
      return (
        (<>
          <Button 
            id="toolbar-resolve-btn"
            className={classes.headerBtn}
            aria-controls="resolve-menu" 
            aria-haspopup="true" 
            variant="contained" 
            color="primary" 
            onClick={(e)=>setAnchorEl(e.currentTarget)}
            startIcon={<Icon path={mdiProgressWrench} size={1} />}
            endIcon={<Icon path={mdiMenuDown} size={1} />}
          >
            {t("requestForm.toolbar.resolve")}
          </Button>
          <Menu
            id="resolve-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            handleClose={()=>setAnchorEl(null)}
            handleBlur={handleBlur}
            content={[
              <MenuItem
                key="resolve-menu-approve"
                id="resolve-menu-approve"
                onClick={()=>handleValidateRequest(()=>{
                  handleCloseSnackbar("snackbarApproved");
                  toggleDialog('dialogApproved', true);
                  setAnchorEl(null);
                })}
              >
                <Typography variant="body2">{t("requestForm.toolbar.approve")}</Typography>
              </MenuItem>,
              <MenuItem
                key="resolve-menu-deny"
                id="resolve-menu-deny"
                onClick={()=>handleValidateRequest(()=>{
                  handleCloseSnackbar("snackbarDenied");
                  toggleDialog('dialogDenied', true);
                  setAnchorEl(null);
                })}
              >
                <Typography variant="body2">{t("requestForm.toolbar.deny")}</Typography>
              </MenuItem>
            ]}
          />
        </>)
      );
    }
  }

  const withdrawBtn=()=>{
    if((!readOnly && 
    request.status.id === RequestStatusId.UpdateRequest) ||
    (isRequester && 
    (request.status.id === RequestStatusId.Submitted || 
    request.status.id === RequestStatusId.DisclosureAnalysis))){
      return (
        <Button
          id="toolbar-withdraw-btn"
          className={classes.headerBtn}
          variant="text"
          color="primary"
          startIcon={<Icon path={mdiUndo} size={1} />}
          onClick={()=>{
            handleCloseSnackbar("snackbarWithdraw");
            toggleDialog("dialogWithdraw", true);
          }}
        >
          {t("requestForm.toolbar.withdraw")}
        </Button>
      );
    }
  }

  const reactivateBtn=()=>{
    if((role===UserRole.Researcher && 
    isRequester &&
    request.status.id === RequestStatusId.Withdrawn) ||
    (role===UserRole.Analyst && 
    (request.status.id === RequestStatusId.Approved ||
    request.status.id === RequestStatusId.Denied))){
      return (
        <Button
          id="toolbar-reactivate-btn"
          className={classes.headerBtn}
          variant="text"
          color="primary"
          startIcon={<Icon path={mdiRestore} size={1} />}
          onClick={()=>{
            handleCloseSnackbar("snackbarReactivate");
            if(role===UserRole.Researcher){
              handleReactivate();
            }else if(isLeadAnalyst || isSupportAnalyst){
              handleReactivate(isLeadAnalyst ? request.primaryAnalyst.phoneNumber : request.supportAnalysts.find(a=>a.account===userInfo.id).phoneNumber);
            }else{
              toggleDialog('dialogAssign', true);
            }
          }}
        >
          {t("requestForm.toolbar.reactivate")}
        </Button>
      );
    }
  }

  const startReviewBtn=()=>{
    if(request.status.id === RequestStatusId.Submitted){
      return (
        <Button
          id="toolbar-startReview-btn"
          className={classes.headerBtn}
          variant="contained"
          color="primary"
          startIcon={<Icon path={mdiPlaySpeed} size={1} />}
          onClick={handleStartReview}
        >
          {t("requestForm.toolbar.startReview")}
        </Button>
      );
    }
  }

  let actionButtons = <></>;
  if(role===UserRole.Researcher){
    actionButtons=<>
      {withdrawBtn()}
      {saveBtn()}
      {submitBtn()}
      {reactivateBtn()}
    </>
  }else if(role===UserRole.Analyst){
    if(isLeadAnalyst){
      actionButtons=<>
        {saveBtn()}
        {startReviewBtn()}
        {requestUpdateBtn()}
        {resolveBtn()}
        {reactivateBtn()}
      </>
    }else{
      actionButtons=<>
        {reactivateBtn()}
      </>
    }
  }

  return <Root>
    <AppBar className={classes.appBar} color="default" position="static">
      <Toolbar position="static">
        <Grid
          container
          justifyContent="space-between"
          className={classes.gridContainer}
        >
          <Grid item>
            <IconButton
              id="toolbar-back-btn"
              edge="start"
              className={classes.menuButton}
              aria-label="Back to dashboard"
              onClick={()=>handleSaveBeforeLeave("/"+i18n.language+"/home", false)}
              size="large">
              <ArrowBackIcon />
            </IconButton>
            <Typography 
              variant="body2" 
              component="span" 
              className={classes.title}
            >
              {t("requestForm.toolbar.dashboard")}
            </Typography>
          </Grid>
          <Grid item className={classes.actions}>
            {actionButtons}
            <Divider orientation="vertical" flexItem />
            <ActionsMenu
              readOnly={readOnly}
              role={role}
              request={request}
              isLeadAnalyst={isLeadAnalyst}
              isSupportAnalyst={isSupportAnalyst}
              handleAssignMe={handleAssignMe}
              handleUnassign={handleUnassign}
              handleDelete={handleDelete}
              handleRefreshForm={handleRefreshForm}
              setBackdropOpen={setBackdropOpen}
              snackbarState={snackbarState}
              handleCloseSnackbar={handleCloseSnackbar}
            />
          </Grid>
        </Grid>
      </Toolbar>
      <DialogUpdate 
        toggleDialog={() => toggleDialog('dialogUpdate', false)} 
        handleUpdate={handleRequestUpdate} 
        open={open.dialogUpdate} 
        snackbar={
          <Snackbar 
            message={snackbarState.message}
            severity={snackbarState.severity}
            open={snackbarState.snackbarUpdate}
            handleClose={()=>handleCloseSnackbar("snackbarUpdate")}
          />
        }
      />
      <DialogApproved 
        toggleDialog={() => toggleDialog('dialogApproved', false)}
        handleApproved={handleApproveRequest} 
        open={open.dialogApproved}
        snackbar={
          <Snackbar 
            message={snackbarState.message}
            severity={snackbarState.severity}
            open={snackbarState.snackbarApproved}
            handleClose={()=>handleCloseSnackbar("snackbarApproved")}
          />
        }
      />
      <DialogDenied 
        toggleDialog={() => toggleDialog('dialogDenied', false)} 
        handleDenied={handleDenyRequest} 
        open={open.dialogDenied}
        snackbar={
          <Snackbar 
            message={snackbarState.message}
            severity={snackbarState.severity}
            open={snackbarState.snackbarDenied}
            handleClose={()=>handleCloseSnackbar("snackbarDenied")}
          />
        }
      />
      <DialogWithdraw 
        toggleDialog={()=>toggleDialog("dialogWithdraw", false)} 
        rowData={{reason_id:0}} 
        handleWithdraw={handleWithdraw} 
        open={open.dialogWithdraw}
        snackbar={
          <Snackbar 
            message={snackbarState.message}
            severity={snackbarState.severity}
            open={snackbarState.snackbarWithdraw}
            handleClose={()=>handleCloseSnackbar("snackbarWithdraw")}
          />
        }
      />
      <DialogAssign
        open={open.dialogAssign}
        toggleDialog={()=>toggleDialog('dialogAssign', false)}
        handleAssignMeAs={handleReactivate}
        isAssignLead={true}
        snackbar={
          <Snackbar 
            message={snackbarState.message}
            severity={snackbarState.severity}
            open={snackbarState.snackbarReactivate}
            handleClose={()=>handleCloseSnackbar("snackbarReactivate")}
          />
        }
      />
    </AppBar>
  </Root>;
}
RequestToolBar.propTypes={
  handleSubmit: PropTypes.func.isRequired,
  handleRequestUpdate: PropTypes.func.isRequired,
  handleApproveRequest: PropTypes.func.isRequired,
  handleDenyRequest: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleValidateRequest: PropTypes.func.isRequired,
  handleAssignMe: PropTypes.func.isRequired,
  handleUnassign: PropTypes.func.isRequired,
  handleSaveBeforeLeave: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleWithdraw: PropTypes.func.isRequired,
  handleReactivate: PropTypes.func.isRequired,
  handleStartReview: PropTypes.func.isRequired,
  isRequester: PropTypes.bool.isRequired,
  isLeadAnalyst: PropTypes.bool.isRequired,
  isSupportAnalyst: PropTypes.bool.isRequired,
  role: PropTypes.number.isRequired,
  readOnly: PropTypes.bool.isRequired,
  request: PropTypes.object.isRequired,
  handleRefreshForm: PropTypes.func.isRequired,
  setBackdropOpen: PropTypes.func.isRequired,
  snackbarState: PropTypes.object.isRequired,
  handleCloseSnackbar: PropTypes.func.isRequired,
}